'use client';

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["component", "colorSet", "buttonSize", "size", "fullWidth", "iconLeading", "iconTrailing", "iconOnly", "children", "className", "as", "UNSAFE_colorSet", "aria-label", "aria-labelledby", "aria-hidden", "hover", "active", "focus"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useContext } from 'react';
import { BrowserDefaultFocusStyleContext } from "../../contexts/BrowserDefaultFocusStyleContext.mjs";
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import { addColorSet, buttonSizesWithDeprecationWarning, controlIconSize, cssColorVar, getButtonFont, getFlatCamelCaseMap, getIconPlacement, getNextControlSize, textClass } from "../../styles/index.mjs";
import { ecx } from "../../utils.mjs";
import { useDefaultProps } from "../../hooks/useDefaultProps.mjs";
import { warn } from "../../logging.mjs";
import { ButtonChildren } from "../../styles/mixins/ButtonChildren/index.mjs";
var ButtonComponent = /*#__PURE__*/React.forwardRef(function ButtonComponent(inProps, ref) {
  var propsWithProviderDefaults = useDefaultProps({
    props: inProps,
    name: 'ButtonPrimary'
  });
  var component = propsWithProviderDefaults.component,
    _propsWithProviderDef = propsWithProviderDefaults.colorSet,
    colorSet = _propsWithProviderDef === void 0 ? 'brightAccent' : _propsWithProviderDef,
    buttonSize = propsWithProviderDefaults.buttonSize,
    _propsWithProviderDef2 = propsWithProviderDefaults.size,
    size = _propsWithProviderDef2 === void 0 ? 'medium' : _propsWithProviderDef2,
    fullWidth = propsWithProviderDefaults.fullWidth,
    iconLeading = propsWithProviderDefaults.iconLeading,
    iconTrailing = propsWithProviderDefaults.iconTrailing,
    iconOnly = propsWithProviderDefaults.iconOnly,
    children = propsWithProviderDefaults.children,
    className = propsWithProviderDefaults.className,
    as = propsWithProviderDefaults.as,
    UNSAFE_colorSet = propsWithProviderDefaults.UNSAFE_colorSet,
    ariaLabel = propsWithProviderDefaults['aria-label'],
    ariaLabelledby = propsWithProviderDefaults['aria-labelledby'],
    ariaHidden = propsWithProviderDefaults['aria-hidden'],
    hover = propsWithProviderDefaults.hover,
    active = propsWithProviderDefaults.active,
    focus = propsWithProviderDefaults.focus,
    props = _objectWithoutProperties(propsWithProviderDefaults, _excluded);
  if (iconOnly && !ariaLabel && !ariaLabelledby && !ariaHidden) {
    warn('ButtonPrimary must have an aria-label or aria-labelledby attribute defined when using the iconOnly prop in order to be accessible.');
  }
  var _useContext = useContext(BrowserDefaultFocusStyleContext),
    useBrowserDefaultFocusStyle = _useContext.useBrowserDefaultFocusStyle;
  var normalizedSize = buttonSize ? getNextControlSize(buttonSize) : size;
  var rel = props.href && props.target === '_blank' ? 'noopener noreferrer' : null;
  var iconPlacement = getIconPlacement({
    iconOnly: iconOnly,
    iconLeading: iconLeading,
    iconTrailing: iconTrailing
  });
  var defaultElement = props.href ? 'a' : 'button';
  var polymorphicProp = as ? as : component;
  var Element = polymorphicProp ? polymorphicProp : defaultElement;

  /**
   * Converts dynamically generated CSS object to CSS vars, which will get rendered
   * inline on the button's inner span.
   */
  var customColorSetStyles = UNSAFE_colorSet ? Object.entries(getFlatCamelCaseMap(UNSAFE_colorSet)).reduce(function (styleObj, token) {
    return _objectSpread(_objectSpread({}, styleObj), {}, _defineProperty({}, cssColorVar(token[0]), token[1]));
  }, {}) : {};
  return /*#__PURE__*/React.createElement(Element, Object.assign({}, props, {
    rel: rel,
    ref: ref,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-hidden": ariaHidden,
    "data-encore-id": ENCORE_DATA_IDS.ButtonPrimary,
    "data-is-icon-only": iconOnly ? true : undefined,
    className: ecx([_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, textClass(getButtonFont(normalizedSize)), !iconOnly), 'button--full-width', fullWidth), 'button--hover', hover), 'button--active', active), 'focus-border', focus || !useBrowserDefaultFocusStyle), 'focus-border--focus', focus), 'button-primary', 'button'])
  }), /*#__PURE__*/React.createElement("span", {
    className: ecx(['baseline', 'overflow-wrap-anywhere', 'button-primary__inner', _defineProperty(_defineProperty(_defineProperty(_defineProperty({}, addColorSet(colorSet), !UNSAFE_colorSet), "button--".concat(size), !iconOnly), "button--".concat(iconPlacement), iconPlacement !== 'none' && !iconOnly), "button-icon-only--".concat(size), !!iconOnly)], className),
    style: customColorSetStyles
  }, /*#__PURE__*/React.createElement(ButtonChildren, {
    iconOnly: iconOnly,
    iconLeading: iconLeading,
    iconTrailing: iconTrailing,
    iconSize: controlIconSize[normalizedSize],
    children: children
  })));
});

/**
 * TODO: Remove when we deprecate `buttonSize` helpers like `ButtonPrimary.sm`.
 */
var buttonHelpers = _objectSpread(_objectSpread({}, buttonSizesWithDeprecationWarning), {}, {
  displayName: 'ButtonPrimary'
});

/**
 * Primary buttons are for the action with the highest priority. They have filled backgrounds and use the bright accent color set by default.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-buttonprimary--docs) |
 * [Encore site](https://encore.spotify.net/web/components/buttons#primary) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/ButtonPrimary) |
 * [Figma](https://www.figma.com/design/bnQXqGkLeiTlbk8JXGDcsX/%E2%9A%99%EF%B8%8F-%E2%80%8A%E2%80%8BEncore-Web-Components?m=auto&node-id=32403-19607&t=B794x4x2a3huupUd-1)
 */
export var ButtonPrimary = Object.assign(ButtonComponent, buttonHelpers);